import { useEffect } from 'react'

import { atendimentoKeys } from '@/features/atendimento/hooks/useAtendimento'
import { meusComunicadosKeys } from '@/features/comunicados/hooks/useMeusComunicados'
import { meusEventosKeys } from '@/features/eventos/hooks/useMeusEventos'
import { meusAvisosFeriasKeys } from '@/features/ferias/hooks/useMeusAvisosFerias'
import { inventarioKeys } from '@/features/inventario/hooks/inventarioKeys'
import { payslipKeys } from '@/features/payslip/hooks/usePayslips'
import { invalidateAtestados } from '@/features/rh/employees/hooks/useEmployeeSubResources'
import { queryClient } from '@/lib/queryClient'
import { authStore } from '@/stores/authStore'

import { syncPushSubscription } from '../services/pushService'
import { swMessageSchema, type PushPayload } from '../types/pushPayload'
import { notificacaoKeys } from './useNotificacoes'

/** Queries a revalidar quando chega um push com o app aberto, além das de notificações. */
function invalidateForPush({ tipo, referenciaId }: PushPayload) {
  switch (tipo) {
    case 'COMUNICADO':
      void queryClient.invalidateQueries({ queryKey: meusComunicadosKeys.all })
      break
    case 'EVENTO':
      void queryClient.invalidateQueries({ queryKey: meusEventosKeys.all })
      break
    case 'ATENDIMENTO':
      // `listas()` cobre abertas e recebidas em qualquer filtro/página.
      void queryClient.invalidateQueries({ queryKey: atendimentoKeys.listas() })
      if (referenciaId) {
        void queryClient.invalidateQueries({ queryKey: atendimentoKeys.detalhe(referenciaId) })
        void queryClient.invalidateQueries({ queryKey: atendimentoKeys.historico(referenciaId) })
      }
      break
    case 'INVENTARIO':
      void queryClient.invalidateQueries({ queryKey: inventarioKeys.meusItens() })
      break
    case 'CONTRACHEQUE':
      void queryClient.invalidateQueries({ queryKey: payslipKeys.meus() })
      break
    case 'ATESTADO':
      // `referenciaId` é o colaborador: revalida os atestados da ficha, se estiver aberta.
      if (referenciaId) void invalidateAtestados(referenciaId)
      break
    case 'FERIAS':
      void queryClient.invalidateQueries({ queryKey: meusAvisosFeriasKeys.all })
      break
  }
}

/**
 * Escuta as mensagens do service worker: push recebido com o app aberto,
 * clique numa notificação e renovação da inscrição. Montado no `App` (acima do
 * router) para funcionar em qualquer tela. `navigate` deve ser estável.
 */
export function usePushServiceWorkerMessages(navigate: (url: string) => void): void {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const onMessage = (event: MessageEvent) => {
      const parsed = swMessageSchema.safeParse(event.data)
      if (!parsed.success) return
      const message = parsed.data

      switch (message.type) {
        case 'PUSH_RECEIVED':
          void queryClient.invalidateQueries({ queryKey: notificacaoKeys.all })
          invalidateForPush(message.payload)
          break
        case 'NAVIGATE':
          navigate(message.url)
          break
        case 'PUSH_SUBSCRIPTION_CHANGED':
          if (authStore.getState().status !== 'authenticated') break
          syncPushSubscription().catch((error: unknown) => {
            console.warn('[push] falha ao reenviar a inscrição renovada', error)
          })
          break
      }
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])
}
